import React, { useEffect, useRef, useState } from "react";
import "./ContactForm.css";
import { contactForm, greeting, skills, socialMediaLinks } from "../../portfolio.js";
import { getTheme } from "../../utils/theme";

const emailLink = (socialMediaLinks.find((s) => s.name === "Gmail") || {}).link || "";
export const EMAIL = emailLink.replace(/^mailto:/, "");

const TYPES = [...skills.data.map((g) => g.title), "Something else"];
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const MIN_MESSAGE = 10;
const MAX_MESSAGE = 1000;
const ENDPOINT = "https://api.web3forms.com/submit";
const ACCESS_KEY = (contactForm && contactForm.web3formsAccessKey) || "";
const APPS_URL = (contactForm && contactForm.appsScriptUrl) || "";
const REMOTE = !!(APPS_URL || ACCESS_KEY); // a delivery service is configured

// hCaptcha is checked by Web3Forms, so it only applies on that path.
const HCAPTCHA_KEY = (contactForm && contactForm.hcaptcha && contactForm.hcaptcha.siteKey) || "";
const HCAPTCHA_ON = !!(contactForm && contactForm.hcaptcha && contactForm.hcaptcha.enabled && HCAPTCHA_KEY && ACCESS_KEY && !APPS_URL);

let hcaptchaLoader = null;
const loadHcaptcha = () => {
  if (window.hcaptcha) return Promise.resolve(window.hcaptcha);
  if (!hcaptchaLoader) {
    hcaptchaLoader = new Promise((resolve, reject) => {
      const s = document.createElement("script");
      s.src = "https://js.hcaptcha.com/1/api.js?render=explicit";
      s.async = true;
      s.defer = true;
      s.onload = () => resolve(window.hcaptcha);
      s.onerror = () => {
        hcaptchaLoader = null; // allow a retry on the next mount
        reject(new Error("hCaptcha failed to load"));
      };
      document.head.appendChild(s);
    });
  }
  return hcaptchaLoader;
};

const validate = (v) => {
  const e = {};
  if (!v.name.trim()) e.name = "Please tell me your name.";
  if (!EMAIL_RE.test(v.email.trim())) e.email = "Enter a valid email address.";
  if (v.message.trim().length < MIN_MESSAGE)
    e.message = `Write at least ${MIN_MESSAGE} characters.`;
  return e;
};

// With a Google Apps Script URL (or a Web3Forms key) configured, messages are delivered to the
// owner's inbox from the page. Without one there is no backend, so the form composes a pre-filled email in the
// visitor's own mail app instead.
export default function ContactForm() {
  const [values, setValues] = useState({ name: "", email: "", type: TYPES[0], message: "" });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [failed, setFailed] = useState(false);
  const [captchaToken, setCaptchaToken] = useState("");
  const [captchaState, setCaptchaState] = useState("loading"); // loading | ready | failed
  const [captchaMissing, setCaptchaMissing] = useState(false);
  const captchaBox = useRef(null);
  const captchaId = useRef(null);

  // Render the hCaptcha widget while the form is showing; redraw when the theme changes.
  useEffect(() => {
    if (!HCAPTCHA_ON || sent) return undefined;
    let dead = false;
    let onTheme = null;
    captchaId.current = null;
    setCaptchaToken("");
    setCaptchaState("loading");

    const draw = (hc) => {
      const box = captchaBox.current;
      if (dead || !box) return;
      if (captchaId.current !== null) {
        try {
          hc.remove(captchaId.current);
        } catch (e) {
          /* already gone */
        }
        box.innerHTML = "";
      }
      setCaptchaToken("");
      captchaId.current = hc.render(box, {
        sitekey: HCAPTCHA_KEY,
        theme: getTheme(),
        size: window.innerWidth < 400 ? "compact" : "normal",
        callback: (t) => {
          setCaptchaToken(t);
          setCaptchaMissing(false);
        },
        "expired-callback": () => setCaptchaToken(""),
        "error-callback": () => setCaptchaToken(""),
      });
      setCaptchaState("ready");
    };

    loadHcaptcha()
      .then((hc) => {
        if (dead) return;
        draw(hc);
        onTheme = () => draw(hc);
        window.addEventListener("themechange", onTheme);
      })
      .catch(() => {
        if (!dead) setCaptchaState("failed");
      });

    return () => {
      dead = true;
      if (onTheme) window.removeEventListener("themechange", onTheme);
    };
  }, [sent]);
  const [copied, setCopied] = useState(false);

  const set = (k) => (e) => {
    const next = { ...values, [k]: e.target.value };
    setValues(next);
    if (touched[k]) setErrors(validate(next));
  };
  const blur = (k) => () => {
    setTouched((t) => ({ ...t, [k]: true }));
    setErrors(validate(values));
  };

  const subject = `Project inquiry: ${values.type}`;
  const body = `Hi ${greeting.title},\n\n${values.message.trim()}\n\n— ${values.name.trim()}\n${values.email.trim()}`;
  const mailto = `mailto:${EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;

  const deliver = async () => {
    setSending(true);
    setFailed(false);
    try {
      if (APPS_URL) {
        // urlencoded body without custom headers: a "simple" request, so no CORS preflight
        const res = await fetch(APPS_URL, {
          method: "POST",
          body: new URLSearchParams({
            name: values.name.trim(),
            email: values.email.trim(),
            topic: values.type,
            message: values.message.trim(),
            page: window.location.href,
            website: "",
          }),
        });
        const data = await res.json().catch(() => ({}));
        if (data.success) setSent(true);
        else setFailed(true);
        return;
      }
      const res = await fetch(ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          access_key: ACCESS_KEY,
          subject,
          from_name: values.name.trim(),
          name: values.name.trim(),
          email: values.email.trim(),
          topic: values.type,
          message: values.message.trim(),
          page: window.location.href,
          botcheck: "",
          ...(HCAPTCHA_ON ? { "h-captcha-response": captchaToken } : {}),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success) setSent(true);
      else setFailed(true);
    } catch (err) {
      setFailed(true);
    } finally {
      setSending(false);
      // a captcha token works once, so ask for a fresh one after every attempt
      if (HCAPTCHA_ON && window.hcaptcha && captchaId.current !== null) {
        try {
          window.hcaptcha.reset(captchaId.current);
        } catch (e) {
          /* widget already gone */
        }
        setCaptchaToken("");
      }
    }
  };

  const submit = (e) => {
    e.preventDefault();
    if (sending) return;
    const errs = validate(values);
    setErrors(errs);
    setTouched({ name: true, email: true, message: true });
    if (Object.keys(errs).length) {
      const first = ["name", "email", "message"].find((k) => errs[k]);
      const el = document.getElementById(`cf-${first}`);
      if (el) el.focus();
      return;
    }
    // honeypot: real visitors never fill the hidden field
    if (e.target.elements.website && e.target.elements.website.value) {
      setSent(true);
      return;
    }
    if (HCAPTCHA_ON && !captchaToken) {
      setCaptchaMissing(true);
      return;
    }
    if (REMOTE) {
      deliver();
    } else {
      setSent(true);
      window.location.href = mailto;
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(`To: ${EMAIL}\nSubject: ${subject}\n\n${body}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      /* clipboard blocked: the mail link still works */
    }
  };

  if (sent && REMOTE) {
    return (
      <div className="cf cf-sent" role="status">
        <div className="cf-check" aria-hidden="true">✓</div>
        <h3>Message sent, {values.name.trim().split(" ")[0]}!</h3>
        <p>
          Thanks for reaching out. Your message is on its way and I usually
          reply within 24 hours at <strong>{values.email.trim()}</strong>.
        </p>
        <div className="cf-actions">
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => {
              setSent(false);
              setValues({ name: "", email: "", type: TYPES[0], message: "" });
              setTouched({});
              setErrors({});
            }}
          >
            Send another message
          </button>
        </div>
      </div>
    );
  }

  if (sent) {
    return (
      <div className="cf cf-sent" role="status">
        <div className="cf-check" aria-hidden="true">✓</div>
        <h3>Almost there, {values.name.trim().split(" ")[0]}!</h3>
        <p>
          Your email app should have opened with the message ready to send. If
          nothing opened, copy it and send it to <strong>{EMAIL}</strong>.
        </p>
        <div className="cf-actions">
          <a className="btn btn-primary" href={mailto}>Open email app</a>
          <button type="button" className="btn btn-ghost" onClick={copy}>
            {copied ? "Copied ✓" : "Copy message"}
          </button>
          <button type="button" className="btn btn-ghost" onClick={() => setSent(false)}>
            Edit message
          </button>
        </div>
      </div>
    );
  }

  const field = (k) => ({
    id: `cf-${k}`,
    value: values[k],
    onChange: set(k),
    onBlur: blur(k),
    "aria-invalid": !!(touched[k] && errors[k]),
    "aria-describedby": touched[k] && errors[k] ? `cf-${k}-err` : undefined,
  });
  const err = (k) =>
    touched[k] && errors[k] ? (
      <span className="cf-err" id={`cf-${k}-err`} role="alert">{errors[k]}</span>
    ) : null;

  return (
    <form className="cf" onSubmit={submit} noValidate>
      <input
        type="text"
        name="website"
        className="cf-hp"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
      />
      <div className="cf-row">
        <div className="cf-field">
          <label htmlFor="cf-name">Your name</label>
          <input type="text" autoComplete="name" placeholder="Jane Doe" {...field("name")} />
          {err("name")}
        </div>
        <div className="cf-field">
          <label htmlFor="cf-email">Email</label>
          <input type="email" autoComplete="email" placeholder="jane@company.com" {...field("email")} />
          {err("email")}
        </div>
      </div>

      <fieldset className="cf-field cf-types">
        <legend>What do you need help with?</legend>
        <div className="cf-chips">
          {TYPES.map((t) => (
            <button
              key={t}
              type="button"
              className={`cf-chip${values.type === t ? " on" : ""}`}
              aria-pressed={values.type === t}
              onClick={() => setValues({ ...values, type: t })}
            >
              {t}
            </button>
          ))}
        </div>
      </fieldset>

      <div className="cf-field">
        <label htmlFor="cf-message">Message</label>
        <textarea
          rows="6"
          maxLength={MAX_MESSAGE}
          placeholder="Tell me about the project, timeline and goals…"
          {...field("message")}
        />
        <div className="cf-foot">
          {err("message") || <span />}
          <span className="cf-count">{values.message.length}/{MAX_MESSAGE}</span>
        </div>
      </div>

      {HCAPTCHA_ON && (
        <div className="cf-captcha">
          <div ref={captchaBox} />
          {captchaState === "failed" && (
            <p className="cf-err" role="alert">
              The captcha could not load (an ad blocker can cause this). Please
              disable it for this page, or <a href={mailto}>email me directly</a>.
            </p>
          )}
          {captchaMissing && !captchaToken && captchaState !== "failed" && (
            <span className="cf-err" role="alert">
              Please complete the captcha above.
            </span>
          )}
        </div>
      )}

      {failed && (
        <div className="cf-alert" role="alert">
          Sorry, the message could not be sent. Please try again, or{" "}
          <a href={mailto}>email me directly</a>.
        </div>
      )}

      <button type="submit" className="btn btn-primary cf-submit" disabled={sending}>
        {sending ? (
          <>
            <span className="cf-spinner" aria-hidden="true" /> Sending…
          </>
        ) : (
          <>
            Send message <span aria-hidden="true">→</span>
          </>
        )}
      </button>
      <p className="cf-note">
        {REMOTE
          ? "Your message goes straight to my inbox. Replies usually within 24 hours."
          : "This opens your email app with the message pre-filled. Replies usually within 24 hours."}
        {HCAPTCHA_ON && (
          <>
            <br />
            Protected by hCaptcha.{" "}
            <a href="https://www.hcaptcha.com/privacy" target="_blank" rel="noopener noreferrer">Privacy</a>
            {" · "}
            <a href="https://www.hcaptcha.com/terms" target="_blank" rel="noopener noreferrer">Terms</a>
          </>
        )}
      </p>
    </form>
  );
}
